import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi, type Adjudication, type BornesDuLeve, type Controle,
  type EditionsPvBornage, type EtatPvBornage, type Qualite, type Releve,
  type VerdictRiverain } from './api';

/**
 * ESPACE DU GÉOMÈTRE (§8) — dépôt, contrôle de qualité, décision.
 *
 * ⚠️ CET ÉCRAN MONTRE LE TÉMOIN DE POPULATION, PAS SEULEMENT LE VERDICT.
 * Chaque contrôle affiche « sur combien d'objets » il a conclu. Un « conforme »
 * obtenu sur 0 candidat n'est pas un contrôle réussi : c'est un contrôle qui
 * n'a rien eu à contredire, et c'est le défaut le plus cher de ce dépôt. Le
 * moteur distingue les deux ; l'écran serait fautif de les afficher pareil.
 *
 * ⚠️ TROIS RÉSULTATS, JAMAIS DEUX. « non évaluable » a sa propre couleur et son
 * propre libellé. Le replier sur « conforme » rendrait vert un levé que
 * personne n'a pu contrôler.
 */

const METHODES: Array<[string, string]> = [
  ['gnss_rtk', 'GNSS RTK (1–10 cm)'],
  ['gnss_statique', 'GNSS statique (3–50 mm)'],
  ['station_totale', 'Station totale (5–100 mm)'],
  ['gps_navigation', 'GPS de navigation (2–15 m)'],
  ['photo_interpretation', 'Photo-interprétation (0,3–5 m)'],
];

const LIBELLE_REGLE: Record<string, string> = {
  geometrie_invalide: 'Validité géométrique',
  sommets_degeneres: 'Sommets dégénérés',
  recouvrement: 'Recouvrement de parcelles',
  lacune: 'Lacune avec une voisine',
  surface_incoherente: 'Cohérence de surface',
  hors_division: 'Situation administrative',
  precision_methode: 'Précision annoncée / méthode',
};

const ETIQUETTE_STATUT: Record<string, string> = {
  brouillon: 'gris', depose: 'ambre', en_controle: 'bleu', valide: 'vert', rejete: 'rouge',
};

export function Geometre({ peutDecider, onOuvrirParcelle }: {
  peutDecider: boolean; onOuvrirParcelle: (identifiant: string) => void;
}) {
  const [liste, setListe] = useState<Releve[]>([]);
  const [filtre, setFiltre] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [depot, setDepot] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try { setListe((await api.releves.liste(filtre || undefined)).releves ?? []); }
    catch (e) {
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "Votre rôle ne permet pas de consulter les levés (cad.releve:lire)."
        : String((e as Error).message ?? e));
    }
  }, [filtre]);

  useEffect(() => { void charger(); }, [charger]);

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>📐 Levés géomètres</h2>
        <div className="actions">
          <select value={filtre} onChange={(e) => setFiltre(e.target.value)} aria-label="Filtrer par statut">
            <option value="">Tous les statuts</option>
            {Object.keys(ETIQUETTE_STATUT).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="bouton primaire" onClick={() => setDepot(true)}>Déposer un levé</button>
        </div>
      </header>

      <div className="avertissement">
        <strong>Un levé validé rend la parcelle opposable.</strong> Il fixe la géométrie, la
        précision et la méthode, et fait passer l'origine à <code>levé terrain</code>.
        Il ne crée, ne transfère et n'éteint <em>aucun droit</em> : cela reste le domaine des
        mutations. Un levé n'est jamais validé par le géomètre qui l'a déposé.
      </div>

      {message && <p className="succes">{message}</p>}
      {erreur && <p className="erreur">{erreur}</p>}

      {depot && <FormulaireDepot
        onFerme={() => setDepot(false)}
        onDepose={(r, q) => {
          setDepot(false);
          setMessage(`Levé ${r.reference} déposé — ${q.conformes} contrôle(s) conforme(s), `
            + `${q.anomalies} anomalie(s), ${q.nonEvaluables} non évaluable(s) sur ${q.candidatsExamines} objet(s) examiné(s).`);
          setOuverte(r.reference);
          void charger();
        }}
        onErreur={setErreur}
      />}

      {liste.length === 0 && !erreur && (
        <p className="mention">Aucun levé. Le registre démarre couvert par le maillage présomptif :
          c'est le levé qui fait passer une parcelle du présomptif à l'opposable.</p>
      )}

      <table className="tableau large">
        <thead>
          <tr><th>Référence</th><th>Statut</th><th>Méthode</th><th>Précision</th>
              <th>Surface</th><th>Parcelle visée</th><th>Anomalies ouvertes</th><th /></tr>
        </thead>
        <tbody>
          {liste.map((r) => (
            <tr key={r.reference}>
              <td><code>{r.reference}</code></td>
              <td><span className={`etiquette ${ETIQUETTE_STATUT[r.statut] ?? 'gris'}`}>{r.statut}</span></td>
              <td>{r.methode}</td>
              <td>{r.precision_annoncee_m} m</td>
              <td>{r.surface_m2 != null ? `${r.surface_m2} m²` : '—'}</td>
              <td>{r.parcelle ? <button className="lien" onClick={() => onOuvrirParcelle(r.parcelle!)}>{r.parcelle}</button> : <em>nouvelle</em>}</td>
              <td>{(r.anomalies_ouvertes ?? 0) > 0
                ? <span className="etiquette rouge">{r.anomalies_ouvertes}</span>
                : <span className="etiquette vert">0</span>}</td>
              <td><button className="bouton discret" onClick={() => setOuverte(r.reference)}>Ouvrir</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      {ouverte && <PanneauLeve
        reference={ouverte} peutDecider={peutDecider}
        onFermer={() => setOuverte(null)}
        onChange={(m) => { setMessage(m); void charger(); }}
      />}
    </div>
  );
}

function FormulaireDepot({ onFerme, onDepose, onErreur }: {
  onFerme: () => void;
  onDepose: (r: Releve, q: Qualite) => void;
  onErreur: (m: string) => void;
}) {
  const [methode, setMethode] = useState('gnss_rtk');
  const [precision, setPrecision] = useState('0.03');
  const [parcelle, setParcelle] = useState('');
  const [surface, setSurface] = useState('');
  const [geojson, setGeojson] = useState('');
  const [observations, setObservations] = useState('');
  const [envoi, setEnvoi] = useState(false);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    let geometrie: unknown;
    try { geometrie = JSON.parse(geojson); }
    catch { onErreur("La géométrie n'est pas un JSON lisible. Collez un objet GeoJSON Polygon ou MultiPolygon."); return; }
    setEnvoi(true);
    try {
      const r = await api.releves.deposer({
        methode, precisionAnnonceeM: Number(precision), geometrie,
        identifiantParcelle: parcelle.trim() || undefined,
        surfaceDeclareeM2: surface.trim() ? Number(surface) : undefined,
        observations: observations.trim() || undefined,
      });
      onDepose(r.releve, r.qualite);
    } catch (err) {
      // ⚠️ Le détail du refus est ce qui permet au géomètre de corriger.
      // Un « échec du dépôt » nu l'obligerait à deviner laquelle de ses
      // vingt bornes est fautive.
      const e2 = err as ErreurApi;
      onErreur(`${e2.message}${e2.details ? ` — ${JSON.stringify(e2.details)}` : ''}`);
    } finally { setEnvoi(false); }
  }

  return (
    <form className="fiche-formulaire" onSubmit={soumettre}>
      <h3>Déposer un levé</h3>
      <div className="champs">
        <label>Méthode
          <select value={methode} onChange={(e) => setMethode(e.target.value)}>
            {METHODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label>Précision annoncée (m)
          <input type="number" step="0.001" min="0.001" value={precision} onChange={(e) => setPrecision(e.target.value)} required />
        </label>
        <label>Parcelle visée <span className="aide">vide = le levé en crée une</span>
          <input value={parcelle} onChange={(e) => setParcelle(e.target.value)} placeholder="GN-PAR-000000007" />
        </label>
        <label>Surface déclarée (m²) <span className="aide">facultatif — confrontée à la mesure</span>
          <input type="number" step="0.01" value={surface} onChange={(e) => setSurface(e.target.value)} />
        </label>
      </div>
      <label>Géométrie levée (GeoJSON Polygon ou MultiPolygon)
        <textarea rows={6} value={geojson} onChange={(e) => setGeojson(e.target.value)} required
          placeholder='{"type":"Polygon","coordinates":[[[-13.014,10.2085],[-13.013,10.2085],[-13.013,10.2095],[-13.014,10.2095],[-13.014,10.2085]]]}' />
      </label>
      <label>Observations
        <textarea rows={2} value={observations} onChange={(e) => setObservations(e.target.value)} />
      </label>
      <p className="mention">
        Les contrôles de qualité tournent <strong>avant</strong> l'enregistrement.
        Une géométrie invalide est refusée avec son motif ; les autres anomalies
        sont enregistrées et vous sont rendues, pour que vous sachiez quoi corriger.
      </p>
      <div className="actions">
        <button type="button" className="bouton discret" onClick={onFerme}>Annuler</button>
        <button className="bouton primaire" disabled={envoi}>{envoi ? 'Contrôle en cours…' : 'Déposer'}</button>
      </div>
    </form>
  );
}

function PanneauLeve({ reference, peutDecider, onFermer, onChange }: {
  reference: string; peutDecider: boolean; onFermer: () => void; onChange: (m: string) => void;
}) {
  const [fiche, setFiche] = useState<{ releve: Record<string, any>; controles: Controle[] } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try { setFiche(await api.releves.fiche(reference)); }
    catch (e) { setErreur(String((e as Error).message ?? e)); }
  }, [reference]);
  useEffect(() => { void charger(); }, [charger]);

  async function decider(decision: 'valide' | 'rejete') {
    /* ⚠️ UN MEMBRE DE LOT NE SE DÉCIDE JAMAIS SEUL (D.1489) : le geste de
       l'écran est alors la décision du LOT, et il le DIT avant de demander le
       motif — le serveur refuserait de toute façon, mais après la saisie. */
    const lot: string | null = fiche?.releve.lot ?? null;
    const objet = lot ? `le lot ${lot} (${fiche?.releve.lot_membres} membre(s), tout-ou-rien)` : reference;
    const motif = window.prompt(decision === 'valide'
      ? `Valider ${objet} — motif consigné au journal :`
      : `Rejeter ${objet} — motif OBLIGATOIRE, il sera notifié au géomètre :`);
    if (motif === null) return;
    if (motif.trim().length < 3) { setErreur('Une décision sans motif ne peut pas être enregistrée.'); return; }
    setOccupe(true); setErreur(null);
    try {
      const r = lot
        ? await api.releves.deciderLot(lot, { decision, motif })
        : await api.releves.decider(reference, { decision, motif });
      onChange(r.message ?? `${reference} → ${r.statut}`);
      await charger();
    } catch (e) {
      const err = e as ErreurApi;
      setErreur(`${err.message}${err.details ? ` — ${JSON.stringify(err.details)}` : ''}`);
    } finally { setOccupe(false); }
  }

  async function rejouer() {
    setOccupe(true); setErreur(null);
    try {
      const r = await api.releves.controler(reference);
      onChange(`${reference} recontrôlé — ${r.qualite.anomalies} anomalie(s) sur ${r.qualite.candidatsExamines} objet(s) examiné(s).`);
      await charger();
    } catch (e) { setErreur(String((e as Error).message ?? e)); }
    finally { setOccupe(false); }
  }

  const ouverts = fiche?.controles.filter((c) => !c.traite_le) ?? [];
  const clos = fiche?.controles.filter((c) => c.traite_le) ?? [];

  return (
    <aside className="panneau">
      <header className="panneau-entete">
        <h3>Levé <code>{reference}</code></h3>
        <button className="fermer" onClick={onFermer} aria-label="Fermer la fiche">×</button>
      </header>

      {erreur && <p className="erreur">{erreur}</p>}
      {!fiche && !erreur && <p className="attente">Chargement…</p>}

      {fiche && (
        <>
          <dl className="champs">
            <div><dt>Statut</dt><dd><span className={`etiquette ${ETIQUETTE_STATUT[fiche.releve.statut] ?? 'gris'}`}>{fiche.releve.statut}</span></dd></div>
            <div><dt>Géomètre</dt><dd>{fiche.releve.geometre ?? '—'}</dd></div>
            <div><dt>Méthode</dt><dd>{fiche.releve.methode} · {fiche.releve.precision_annoncee_m} m</dd></div>
            <div><dt>Surface mesurée</dt><dd>{fiche.releve.surface_m2} m² · périmètre {fiche.releve.perimetre_m} m</dd></div>
            <div><dt>Parcelle visée</dt><dd>{fiche.releve.parcelle ?? <em>nouvelle parcelle</em>}</dd></div>
            <div><dt>Parcelle produite</dt>
              <dd>{fiche.releve.parcelle_produite
                ? <code>{fiche.releve.parcelle_produite}</code>
                : <em>aucune — le levé n'a encore produit aucun effet</em>}</dd></div>
            {fiche.releve.lot && (
              <div><dt>Lot solidaire</dt>
                <dd><code>{fiche.releve.lot}</code> — {fiche.releve.lot_membres} membre(s).
                  La décision porte sur le lot <strong>entier</strong>.</dd></div>
            )}
            {fiche.releve.motif_decision && <div><dt>Décision</dt><dd>{fiche.releve.motif_decision}</dd></div>}
          </dl>

          <Riverains reference={reference} statut={fiche.releve.statut}
                     premiereImmatriculation={fiche.releve.parcelle == null}
                     onChange={() => void charger()} />

          {/* ⚠️ LES BORNES AVANT LE PV, parce que le PV les IMPRIME. Inscrire
              après avoir délivré la pièce ferait circuler un procès-verbal qui
              annonce « 0 borne » sur un bornage qui en porte quatre. */}
          <SectionBornes reference={reference} statutReleve={fiche.releve.statut} />

          {/* ⚠️ APRÈS le recueil des avis, et ce n'est pas un rangement : le PV
              CONSTATE ce que le bloc ci-dessus a recueilli. Le placer avant
              inviterait à délivrer la pièce puis à continuer d'entendre des
              voisins — c'est-à-dire à faire circuler un papier périmé. */}
          <SectionProcesVerbal reference={reference} statutReleve={fiche.releve.statut} />

          <h4>Contrôles de qualité ({ouverts.length} ouvert{ouverts.length > 1 ? 's' : ''})</h4>
          <ListeControles controles={ouverts} />

          {clos.length > 0 && (
            <details>
              <summary>{clos.length} contrôle(s) clos — passages précédents</summary>
              <p className="mention">
                Conservés, jamais supprimés : le registre doit pouvoir montrer qu'une
                anomalie a existé et quand elle a été levée.
              </p>
              <ListeControles controles={clos} />
            </details>
          )}

          <div className="actions">
            <button className="bouton discret" disabled={occupe || fiche.releve.statut === 'valide'} onClick={rejouer}>
              Rejouer les contrôles
            </button>
            {peutDecider && fiche.releve.statut !== 'valide' && fiche.releve.statut !== 'rejete' && (
              <>
                <button className="bouton" disabled={occupe} onClick={() => decider('rejete')}>
                  {fiche.releve.lot ? 'Rejeter le lot' : 'Rejeter'}</button>
                <button className="bouton primaire" disabled={occupe} onClick={() => decider('valide')}>
                  {fiche.releve.lot ? `Valider le lot (${fiche.releve.lot_membres})` : 'Valider'}</button>
              </>
            )}
          </div>
          {!peutDecider && (
            <p className="mention">
              Votre rôle ne permet pas de décider (<code>cad.releve:valider</code>).
              Un levé est contrôlé par un tiers — jamais par son auteur.
            </p>
          )}
        </>
      )}
    </aside>
  );
}

/**
 * LES BORNES DU LEVÉ — inscrire au registre ce qui a été posé sur le terrain.
 *
 * ⚠️⚠️ CE BLOC EXISTE PARCE QUE `cad.borne` N'AVAIT AUCUN ÉCRIVAIN. Elle est
 * lue par le plan cadastral, le plan de bornage, le plan expert et le
 * procès-verbal — et **0 ligne pour 988 695 parcelles**. Le générateur de plan
 * de bornage est livré depuis le 15/09 ; aucun plan n'était délivrable dans le
 * pays, faute d'une seule borne au registre.
 *
 * ⚠️ ON N'INSCRIT QUE SUR UN POINT MESURÉ. L'écran ne propose jamais de saisir
 * des coordonnées : chaque ligne du formulaire est attachée à un point du levé.
 * Permettre une position libre reviendrait à déplacer une limite de propriété
 * par un formulaire.
 */
function SectionBornes({ reference, statutReleve }: {
  reference: string; statutReleve: string;
}) {
  const [etat, setEtat] = useState<BornesDuLeve | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saisie, setSaisie] = useState<Record<number, {
    numero: string; type: string; cote: string; observations: string;
  }>>({});
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    setErreur(null);
    try { setEtat(await api.releves.bornes.lire(reference)); }
    catch (e) { setErreur(String((e as Error).message ?? e)); }
  }, [reference]);
  useEffect(() => { void charger(); }, [charger, statutReleve]);

  function modifier(indice: number, champ: string, valeur: string) {
    setSaisie((s) => ({
      ...s,
      [indice]: { numero: '', type: '', cote: '', observations: '', ...s[indice], [champ]: valeur },
    }));
  }

  async function inscrire() {
    const lignes = Object.entries(saisie)
      .filter(([, v]) => v.numero.trim() && v.type)
      .map(([indice, v]) => ({
        indicePoint: Number(indice), numero: v.numero.trim(), type: v.type,
        /**
         * ⚠️ `posee` ET NON UN CHOIX PAR DÉFAUT MUET. Un géomètre qui inscrit
         * depuis son levé constate une pose ; les quatre autres états
         * (retrouvée, détruite, rétablie, déplacée) décrivent une visite
         * ultérieure et ne s'inscrivent pas par ce formulaire.
         */
        etat: 'posee',
        ...(v.cote.trim() ? { cote: v.cote.trim() } : {}),
        ...(v.observations.trim() ? { observations: v.observations.trim() } : {}),
      }));

    if (lignes.length === 0) {
      setErreur('Aucune borne à inscrire : un numéro et un type sont requis pour chaque point.');
      return;
    }
    const motif = window.prompt(
      `Inscrire ${lignes.length} borne(s) au registre pour ${reference}.\n\n`
      + `Ce que le registre retient d'une borne engage une limite de propriété.\n`
      + `Motif (10 caractères minimum, consigné au journal) :`);
    if (motif === null) return;
    if (motif.trim().length < 10) { setErreur('Un motif d\'au moins 10 caractères est exigé.'); return; }

    setOccupe(true); setErreur(null); setMessage(null);
    try {
      const r = await api.releves.bornes.inscrire(reference, { motif: motif.trim(), bornes: lignes });
      setMessage(r.message);
      setSaisie({});
      await charger();
    } catch (e) {
      const err = e as ErreurApi;
      setErreur(`${err.message}${err.details ? ` — ${JSON.stringify(err.details)}` : ''}`);
    } finally { setOccupe(false); }
  }

  const sansBorne = etat?.points.filter((p) => p.borne === null) ?? [];

  return (
    <section className="piece-bornes">
      <h4>Bornes du levé</h4>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {message && <p className="succes" role="status">{message}</p>}
      {!etat && !erreur && <p className="attente">Lecture des points mesurés…</p>}

      {etat && (
        <>
          <p className="mention">
            {etat.points.length} point(s) mesuré(s) · <strong>{etat.inscrites}</strong> borne(s)
            inscrite(s) au registre
            {/* ⚠️ LE RESTE EST DIT, PAS DÉDUIT : un plan de bornage délivré sur
                un bornage partiel figure une limite incomplète. */}
            {sansBorne.length > 0 && <> · {sansBorne.length} point(s) sans borne</>}
          </p>

          {/* ⚠️ LE MOTIF VIENT DU SERVICE, MOT POUR MOT — et il distingue
              « brouillon » de « aucun point mesuré », deux causes, deux suites. */}
          {!etat.inscriptible && etat.motifNonInscriptible && (
            <p className="note-indispo">{etat.motifNonInscriptible}</p>
          )}

          {etat.points.length > 0 && (
            <ul className="liste-bornes">
              {etat.points.map((p) => (
                <li key={p.indice} data-inscrite={p.borne ? 'oui' : 'non'}>
                  <strong>Point {p.indice}</strong>{' '}
                  <span className="mention">
                    {p.latitude.toFixed(6)}, {p.longitude.toFixed(6)}
                  </span>
                  {p.borne ? (
                    <>
                      <br />
                      <span className="etiquette vert">{p.borne.numero}</span>{' '}
                      {p.borne.type} · {p.borne.etat}
                      {p.borne.cote && <> · côté {p.borne.cote}</>}
                      {p.borne.observations && (
                        <><br /><span className="mention">{p.borne.observations}</span></>
                      )}
                    </>
                  ) : etat.inscriptible ? (
                    <div className="saisie-borne">
                      <input type="text" placeholder="N° (12 car. max)" maxLength={12}
                             aria-label={`Numéro de la borne du point ${p.indice}`}
                             value={saisie[p.indice]?.numero ?? ''}
                             onChange={(e) => modifier(p.indice, 'numero', e.target.value)} />
                      <select aria-label={`Type de la borne du point ${p.indice}`}
                              value={saisie[p.indice]?.type ?? ''}
                              onChange={(e) => modifier(p.indice, 'type', e.target.value)}>
                        <option value="">— nature constatée —</option>
                        {/* ⚠️ LE CODE EN VALEUR, LE LIBELLÉ À L'ÉCRAN, les deux
                            venus du service. L'écran rendait lui-même le code
                            lisible (`replace(/_/g, ' ')`) : un second
                            vocabulaire, qui a fini imprimé sur un plan d'État. */}
                        {etat.typesBorne.map((t) => (
                          <option key={t.code} value={t.code}>{t.libelle}</option>
                        ))}
                      </select>
                      <input type="text" placeholder="côté (facultatif)" maxLength={40}
                             aria-label={`Côté de la borne du point ${p.indice}`}
                             value={saisie[p.indice]?.cote ?? ''}
                             onChange={(e) => modifier(p.indice, 'cote', e.target.value)} />
                      <input type="text" placeholder="observations (facultatif)" maxLength={500}
                             aria-label={`Observations sur la borne du point ${p.indice}`}
                             value={saisie[p.indice]?.observations ?? ''}
                             onChange={(e) => modifier(p.indice, 'observations', e.target.value)} />
                    </div>
                  ) : (
                    <><br /><span className="mention">Aucune borne inscrite.</span></>
                  )}
                </li>
              ))}
            </ul>
          )}

          {etat.inscriptible && sansBorne.length > 0 && (
            <button type="button" className="bouton primaire" disabled={occupe}
                    onClick={() => void inscrire()}>
              {occupe ? 'Inscription…' : 'Inscrire les bornes renseignées'}
            </button>
          )}
        </>
      )}
    </section>
  );
}

/**
 * §6 — LE PROCÈS-VERBAL DE BORNAGE CONTRADICTOIRE, DEPUIS LE LEVÉ.
 *
 * ⚠️ CE QU'IL AJOUTE AU BLOC « RIVERAINS » JUSTE AU-DESSUS. Ce bloc-là sert à
 * RECUEILLIR ; celui-ci CONSTATE, et le constat est une pièce opposable écrite
 * au coffre. Un agent doit voir le verdict AVANT de cliquer : découvrir dans le
 * PDF qu'un voisin s'est opposé, c'est le découvrir après avoir promis la pièce
 * à un usager — et parfois après la lui avoir remise.
 *
 * ⚠️ L'ÉCRAN NE TRADUIT PAS `nature` EN LIBELLÉ. Le service rend `detail` (ce
 * qui a été vu) et `suite` (ce que cela appelle) en clair ; recopier ici une
 * table des natures créerait un second vocabulaire qui divergerait du premier
 * correctif appliqué d'un seul côté. `nature` ne sert qu'à marquer la ligne.
 */
function SectionProcesVerbal({ reference, statutReleve }: {
  reference: string; statutReleve: string;
}) {
  const [etat, setEtat] = useState<EtatPvBornage | null>(null);
  const [editions, setEditions] = useState<EditionsPvBornage | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setEtat(null); setEditions(null); setErreur(null);
    /**
     * ⚠️ `allSettled`, PAS `all` : les deux appels sont indépendants. Un levé
     * dont aucun PV n'a jamais été délivré ne doit pas faire disparaître le
     * verdict de l'écran.
     */
    Promise.allSettled([
      api.releves.procesVerbal.etat(reference),
      api.releves.procesVerbal.editions(reference),
    ]).then(([e, v]) => {
      if (e.status === 'fulfilled') setEtat(e.value);
      else setErreur(String((e.reason as Error)?.message ?? e.reason));
      if (v.status === 'fulfilled') setEditions(v.value);
    });
  }, [reference, statutReleve]);

  const sansMoteur = etat !== null && !etat.rendudisponible;
  const bloque = etat !== null && (!etat.editable || sansMoteur);

  return (
    <section className="piece-pv">
      <h4>Procès-verbal de bornage</h4>
      {erreur && <p className="erreur" role="alert">{erreur}</p>}
      {!etat && !erreur && <p className="attente">Lecture du contradictoire…</p>}

      {etat && (
        <>
          <p>
            <span className={`etiquette ${etat.contradictoire.etat === 'acquis' ? 'vert'
              : etat.contradictoire.etat === 'non_acquis' ? 'rouge' : 'gris'}`}
                  data-etat={etat.contradictoire.etat}>
              {etat.contradictoire.etat === 'acquis' ? 'Contradictoire acquis'
                : etat.contradictoire.etat === 'non_acquis' ? 'Contradictoire NON acquis'
                : 'Sans objet'}
            </span>
          </p>

          {/* ⚠️ LA PHRASE DU SERVICE, TELLE QUELLE. Elle dit sur quelle
              population porte le verdict — « aucun obstacle » est trivialement
              vrai quand aucun avis n'a été recueilli. La résumer la viderait. */}
          <p className="mention">{etat.contradictoire.lecture}</p>

          <ul className="pv-comptes">
            <li>Contiguës attendues : <strong>{etat.contradictoire.attendues}</strong></li>
            <li>Avis recueillis : <strong>{etat.contradictoire.recueillis}</strong></li>
            <li>Accords : {etat.contradictoire.accords} · Contestations : {etat.contradictoire.contestations}
              {' '}· Non aboutis : {etat.contradictoire.nonAboutis}</li>
          </ul>

          {etat.contradictoire.obstacles.length > 0 && (
            <ul className="pv-obstacles">
              {etat.contradictoire.obstacles.map((o) => (
                <li key={o.nature} data-nature={o.nature}>
                  <strong>{o.compte}</strong> — {o.detail}
                  <br /><span className="mention">{o.suite}</span>
                </li>
              ))}
            </ul>
          )}

          {/* ⚠️ LES POINTS DU LEVÉ NE SONT PAS DES BORNES, et l'écran ne doit
              pas les confondre : `cad.borne` est vide pour tout le pays, alors
              que les levés portent des points mesurés. « 0 borne » seul ferait
              croire que rien n'a été relevé sur le terrain. */}
          <p className="mention">
            Bornes inscrites au registre : <strong>{etat.bornes.length}</strong>
            {' · '}points mesurés dans la géométrie du levé : <strong>{etat.pointsGeometrieLeve}</strong>
            {etat.bornes.length === 0 && etat.pointsGeometrieLeve > 0 && (
              <> — les points existent, ils n'ont jamais été inscrits comme bornes.</>
            )}
          </p>

          {etat.comparants.length > 0 && (
            <ul className="pv-comparants">
              {etat.comparants.map((c, i) => (
                <li key={`${c.cote}-${i}`} data-opposable={c.opposable ? 'oui' : 'non'}>
                  <strong>{c.cote}</strong> · {c.nom}
                  {' '}<span className="mention">({c.source === 'registre' ? 'titulaire inscrit' : 'nom déclaré sur le terrain'})</span>
                  {' — '}{c.verdict}
                  {c.motif && <><br /><span className="mention">« {c.motif} »</span></>}
                  {/* ⚠️ UN AVIS NON OPPOSABLE N'EST PAS UN AVIS MANQUANT : il a
                      été donné, il ne peut simplement pas fonder une
                      adjudication. Les afficher pareil enverrait au mauvais
                      guichet — reconvoquer au lieu d'établir l'identité. */}
                  {!c.opposable && (
                    <><br /><span className="alerte-trace">
                      ⚠ Identité non établie : cet avis ne peut pas fonder l'adjudication.
                    </span></>
                  )}
                </li>
              ))}
            </ul>
          )}

          {etat.manques.length > 0 && (
            <>
              <p className="mention">Ce procès-verbal ne pourra PAS établir :</p>
              <ul className="motifs">{etat.manques.map((m) => <li key={m}>{m}</li>)}</ul>
            </>
          )}
          {etat.reserves.length > 0 && (
            <ul className="reserves">{etat.reserves.map((r) => <li key={r}>{r}</li>)}</ul>
          )}

          {/* ⚠️ LE MOTIF VIENT DU SERVICE, MOT POUR MOT. L'écran qui
              reformulerait un refus tiendrait une seconde version de la règle. */}
          {!etat.editable && etat.motifNonEditable && (
            <p className="erreur" role="alert">{etat.motifNonEditable}</p>
          )}
          {etat.editable && sansMoteur && (
            <p className="erreur" role="alert">
              Le moteur d'actes n'est pas configuré : ce procès-verbal ne peut pas être
              délivré. Son contenu reste lisible ci-dessus, sans valeur de document.
            </p>
          )}

          <a className={`bouton primaire${bloque ? ' desactive' : ''}`}
             href={bloque ? undefined : api.releves.procesVerbal.url(reference)}
             target="_blank" rel="noreferrer" aria-disabled={bloque}
             onClick={(e) => { if (bloque) e.preventDefault(); }}>
            Délivrer le procès-verbal
          </a>
        </>
      )}

      {editions && editions.editions.length === 0 && (
        <p className="vide">Aucun procès-verbal délivré pour ce levé.</p>
      )}
      {editions && editions.editions.length > 0 && (
        <ul className="liste-editions">
          {editions.editions.map((e) => (
            <li key={e.version} data-trace={e.avisInchanges === false ? 'modifie' : 'inchange'}>
              <strong>V{e.version}</strong> · {e.etatContradictoire}
              <br />
              <span className="mention">
                {new Date(e.editeLe).toLocaleString('fr-FR')} — {e.document}
              </span>
              {e.avisInchanges === false && (
                <>
                  <br />
                  <span className="alerte-trace">
                    ⚠ Les avis recueillis ont changé depuis : ce papier décrit un
                    contradictoire qui n'est plus le dernier.
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ListeControles({ controles }: { controles: Controle[] }) {
  if (controles.length === 0) return <p className="mention">Aucun contrôle enregistré.</p>;
  return (
    <ul className="controles">
      {controles.map((c, i) => {
        const examines = c.candidatsExamines ?? c.candidats_examines ?? 0;
        const couleur = c.resultat === 'conforme' ? 'vert' : c.resultat === 'anomalie' ? 'rouge' : 'gris';
        return (
          <li key={`${c.regle}-${i}`} className={`controle ${couleur}`}>
            <div className="controle-tete">
              <strong>{LIBELLE_REGLE[c.regle] ?? c.regle}</strong>
              <span className={`etiquette ${couleur}`}>
                {c.resultat === 'non_evaluable' ? 'non évaluable' : c.resultat}
                {c.gravite ? ` · gravité ${c.gravite}` : ''}
              </span>
            </div>
            {/* ⚠️ LE TÉMOIN DE POPULATION, VISIBLE. « conforme sur 0 objet
                examiné » n'est pas un contrôle réussi, et l'agent doit
                pouvoir le voir sans ouvrir la base. */}
            <p className="temoin">
              {examines} objet(s) examiné(s)
              {examines === 0 && c.resultat === 'conforme' && ' — attention : rien à contredire'}
            </p>
            <pre className="brut">{JSON.stringify(c.detail, null, 2)}</pre>
          </li>
        );
      })}
    </ul>
  );
}


/**
 * ADJUDICATION CONTRADICTOIRE — l'avis des voisins sur le contour proposé.
 *
 * ⚠️ CE QUI SÉPARE UN RELEVÉ D'UN BORNAGE. Un relevé mesure ; un bornage fait
 * CONVENIR. La doctrine *fit-for-purpose* — celle qui a permis au Rwanda
 * d'enregistrer tout son territoire en quelques années — ne repose pas sur le
 * centimètre, elle repose sur l'accord des voisins, recueilli sur place.
 *
 * ⚠️ LE NOMBRE ATTENDU EST AFFICHÉ AVANT LE NOMBRE RECUEILLI, et ce n'est pas
 * cosmétique : « 3 avis recueillis » ne veut rien dire sans « sur combien ».
 * Un écran qui n'affiche que les signatures obtenues laisse croire à un accord
 * là où personne n'a été convoqué.
 */
function Riverains({ reference, statut, premiereImmatriculation, onChange }: {
  reference: string; statut: string; premiereImmatriculation: boolean; onChange: () => void;
}) {
  const [a, setA] = useState<Adjudication | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [saisie, setSaisie] = useState<{ titulaireId: string; parcelleVoisineId: string; cote: string; verdict: VerdictRiverain; motif: string }>(
    { titulaireId: '', parcelleVoisineId: '', cote: '', verdict: 'concordant', motif: '' });

  const charger = useCallback(async () => {
    try { setA(await api.releves.riverains(reference)); setErreur(null); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : 'Lecture impossible'); }
  }, [reference]);
  useEffect(() => { void charger(); }, [charger]);

  if (!premiereImmatriculation) return null;   // le contradictoire porte sur la 1re immatriculation
  if (erreur) return <p className="erreur">{erreur}</p>;
  if (!a) return <p className="attente">Lecture de l'adjudication…</p>;

  const clos = statut === 'valide' || statut === 'rejete';
  const desaccord = saisie.verdict === 'opposition' || saisie.verdict === 'refus_de_signer';

  async function recueillir(e: React.FormEvent) {
    e.preventDefault();
    setEnvoi(true);
    try {
      const r = await api.releves.recueillir(reference, {
        titulaireId: saisie.titulaireId,
        parcelleVoisineId: saisie.parcelleVoisineId || undefined,
        cote: saisie.cote, verdict: saisie.verdict,
        motif: saisie.motif || undefined,
      });
      setErreur(r.avertissement);           // un avertissement n'est pas une erreur, mais il se lit
      setSaisie({ titulaireId: '', parcelleVoisineId: '', cote: '', verdict: 'concordant', motif: '' });
      await charger(); onChange();
    } catch (err) {
      setErreur(err instanceof ErreurApi ? err.message : "Recueil impossible");
    } finally { setEnvoi(false); }
  }

  return (
    <section className="adjudication">
      <h4>
        Adjudication contradictoire —{' '}
        <strong>{a.recueillis}</strong> avis sur <strong>{a.attendus}</strong> parcelle(s) contiguë(s)
      </h4>
      <p className={a.adjudication_acquise ? 'rien-a-signaler' : 'indetermine'}>{a.lecture}</p>

      {a.parcelles_non_entendues.length > 0 && (
        <p className="erreur">
          Non entendues : {a.parcelles_non_entendues.map((p) => p.identifiant).join(' · ')}.
          Le levé ne pourra pas être validé.
        </p>
      )}
      {a.sans_identite.length > 0 && (
        <p className="erreur">
          Sans NIN ni identité vérifiée : {a.sans_identite.map((r) => r.denomination).join(' · ')}.
          Un consentement anonyme n'engage personne.
        </p>
      )}
      {a.bloquants.length > 0 && (
        <p className="erreur">
          {a.bloquants.map((r) => `${r.denomination} — ${LIBELLE_VERDICT[r.verdict]}`).join(' · ')}.
          Instruire l'opposition ou reconvoquer.
        </p>
      )}

      {a.riverains.length > 0 && (
        <table className="tableau">
          <thead>
            <tr><th>Voisin</th><th>Né le · lieu</th><th>Côté</th><th>Avis</th><th>Identité</th></tr>
          </thead>
          <tbody>
            {a.riverains.map((r) => (
              <tr key={r.id}>
                <td>{r.denomination}{r.parcelle_voisine && <em className="mention"> · {r.parcelle_voisine}</em>}</td>
                {/* ⚠️ Décodés du NIN, pas ressaisis : le numéro guinéen les porte. */}
                <td>{r.nin_date_naissance
                  ? `${new Date(r.nin_date_naissance).toLocaleDateString('fr-FR')}${r.nin_code_lieu ? ` · ${r.nin_code_lieu}` : ''}`
                  : <span className="masque">non renseigné</span>}</td>
                <td>{r.cote}</td>
                <td>
                  <span className={`etiquette ${r.verdict === 'concordant' ? 'vert' : 'rouge'}`}>
                    {LIBELLE_VERDICT[r.verdict]}
                  </span>
                  {r.motif && <em className="mention"> — {r.motif}</em>}
                </td>
                <td>{r.porte_un_nin ? 'NIN' : r.identite_verifiee ? 'vérifiée' : <strong className="erreur">aucune</strong>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!clos && (ouvert ? (
        <form className="filtres" onSubmit={recueillir}>
          <label>Titulaire (UUID)
            <input required value={saisie.titulaireId}
                   onChange={(e) => setSaisie({ ...saisie, titulaireId: e.target.value })}
                   placeholder="identifiant du voisin au registre" />
          </label>
          <label>Parcelle voisine (UUID, si connue)
            <input value={saisie.parcelleVoisineId}
                   onChange={(e) => setSaisie({ ...saisie, parcelleVoisineId: e.target.value })} />
          </label>
          <label>Côté
            <input required minLength={2} value={saisie.cote}
                   onChange={(e) => setSaisie({ ...saisie, cote: e.target.value })}
                   placeholder="nord, le long du sentier, segment B03-B04…" />
          </label>
          <label>Avis
            <select value={saisie.verdict}
                    onChange={(e) => setSaisie({ ...saisie, verdict: e.target.value as VerdictRiverain })}>
              {Object.entries(LIBELLE_VERDICT).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          {desaccord && (
            <label>Motif — obligatoire
              <input required minLength={10} value={saisie.motif}
                     onChange={(e) => setSaisie({ ...saisie, motif: e.target.value })}
                     placeholder="où passe la limite contestée, et pourquoi" />
            </label>
          )}
          <button className="bouton primaire" disabled={envoi}>{envoi ? 'Enregistrement…' : 'Recueillir'}</button>
          <button type="button" className="bouton discret" onClick={() => setOuvert(false)}>Annuler</button>
        </form>
      ) : (
        <button className="bouton" onClick={() => setOuvert(true)}>Recueillir l'avis d'un voisin</button>
      ))}
    </section>
  );
}

/** ⚠️ QUATRE ÉTATS, PAS DEUX : « absent » et « refus de signer » ne sont pas des
 *  oppositions — ils disent que le contradictoire n'a PAS EU LIEU, ce qui appelle
 *  une reconvocation et non l'instruction d'un litige. */
const LIBELLE_VERDICT: Record<VerdictRiverain, string> = {
  concordant: 'Concordant',
  opposition: 'Opposition',
  absent: 'Absent',
  refus_de_signer: 'Refus de signer',
};
